import { TriangleAlert } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { isApiError, type ProjectSummary, useDeleteAdminProject } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

/** The backend's own minimum for the override reason (`min_length=10`). */
const MIN_REASON_LENGTH = 10;

interface ProjectDeleteDialogProps {
  project: ProjectSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The live app registrations a refused delete named, if that is why it was refused.
 *
 * The server answers a force-delete of a project that still holds them with a
 * 409 whose `detail.app_registrations` lists them: deleting one is
 * irreversible, so it takes a decommission first, or a recorded override.
 */
function liveRegistrations(err: unknown): string[] | null {
  if (!isApiError(err)) {
    return null;
  }
  const detail = (err.body as { detail?: unknown } | undefined)?.detail;
  if (detail === null || typeof detail !== "object" || Array.isArray(detail)) {
    return null;
  }
  const names = (detail as { app_registrations?: unknown }).app_registrations;
  return Array.isArray(names) ? names.map(String) : null;
}

export function ProjectDeleteDialog({ project, open, onOpenChange }: ProjectDeleteDialogProps) {
  const deleteProject = useDeleteAdminProject();
  const [error, setError] = useState("");
  const [registrations, setRegistrations] = useState<string[] | null>(null);
  const [reason, setReason] = useState("");
  const deleting = deleteProject.isPending;
  const overriding = registrations !== null;
  const canDelete = !deleting && (!overriding || reason.trim().length >= MIN_REASON_LENGTH);

  const handleDelete = async (e: React.MouseEvent) => {
    // AlertDialogAction closes the dialog on click by default — prevent that
    // so a failed delete keeps the dialog open with the error visible, and
    // close manually on success instead.
    e.preventDefault();
    if (!project || !canDelete) {
      return;
    }
    setError("");

    try {
      await deleteProject.mutateAsync({
        projectId: project.id,
        appRegistrationsReason: overriding ? reason.trim() : undefined,
      });
      onOpenChange(false);
    } catch (err) {
      logger.error("[project-delete] failed:", err);
      const live = liveRegistrations(err);
      if (live) {
        setRegistrations(live);
        return;
      }
      setError(readApiErrorMessage(err, "Failed to delete project. Please try again."));
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Don't let a stale error or override greet the next open.
      setError("");
      setRegistrations(null);
      setReason("");
    }
    onOpenChange(next);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Project</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{project?.project_name}</strong>? This removes
            the project record from the database only — verify its GitHub repo is already gone (or
            clean it up first), because it is not touched by this action.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {overriding && (
          <div className="space-y-3">
            <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40">
              <p className="flex items-start gap-2 text-sm font-medium text-red-800 dark:text-red-300">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                This project still holds live app registrations
              </p>
              <ul className="mt-1 ml-5 list-disc text-xs text-red-800/90 dark:text-red-300/90">
                {registrations.map((name) => (
                  <li key={name} className="font-mono">
                    {name}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-red-800/90 dark:text-red-300/90">
                Decommission them first (App Registrations, a recorded decision each), or give a
                reason below to delete them from Entra with the project. Deleting an application is
                irreversible for whatever uses it; your reason is recorded in the activity log.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="delete-override-reason">Reason for deleting them (required)</Label>
              <Textarea
                id="delete-override-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                disabled={deleting}
              />
            </div>
          </div>
        )}

        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={handleDelete} disabled={!canDelete}>
            {deleting
              ? "Deleting..."
              : overriding
                ? "Delete project and its app registrations"
                : "Delete Project"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
